/**
 * Le pagine che si leggono al banco.
 *
 * Chi legge è in piedi, ha le mani occupate e sta cercando una cosa sola. Quindi
 * frasi corte, verbi all'imperativo, e niente su come funziona dentro: non è una
 * spiegazione, è un promemoria.
 *
 * Il perimetro è dichiarato e stretto: **ricezione e carico del magazzino**. Il
 * carico è entrato il 01/09/2026, quando ha smesso di girare su cassette
 * inventate e ha cominciato a ricevere quelle vere dal banco.
 *
 * Restano fuori scarico, produzione e fine linea: hanno le schermate ma girano
 * su dati cablati in memoria (`apps/api/src/flow/sample-data.ts`), e
 * raccontarle come vere prometterebbe al reparto cose che il magazzino non sa
 * ancora fare. Si aggiungono quando saranno collegate.
 *
 * **L'eccezione, e le condizioni che la reggono.** `banco-express` racconta i
 * prelievi, che collegati non sono: `express: false` è cablato su ogni ordine
 * in `apps/api/src/flow/planning/deconetwork-source.ts` e `daNegozio` resta
 * vuoto in `packages/domain/src/planning/plan-compose.ts`, quindi la schermata
 * dice sempre «Nessun ordine fermo». Sta dentro lo stesso perché il reparto la
 * schermata la **vede nel menu** e chiede cosa sia, e un vuoto senza spiegazione
 * si legge come un guasto. Il prezzo si paga in testa alla pagina, con la prima
 * sezione che dichiara di non essere attiva e un test che la pretende. Quando i
 * due campi diventeranno veri, l'avviso va tolto e il test cambiato: sono la
 * stessa modifica.
 *
 * I titoli dei rifiuti sono **copiati** dai `refuse(...)` del dominio: chi apre
 * la guida sta cercando la frase esatta che ha appena letto a schermo.
 */
/** Le pagine che tutti vedono: non esiste un valore «chiunque». */
const TUTTI = ['ricezione', 'produzione', 'magazzino', 'fine-linea', 'amministratore'];
export const PAGINE_BANCO = [
    {
        k: 'banco-da-dove-parto',
        station: 'banco',
        title: 'Da dove parto',
        summary: 'Ho della merce davanti. Quale schermata, e in che ordine.',
        roles: TUTTI,
        sections: [
            {
                title: 'Guarda cosa hai in mano, non cosa devi fare',
                body: [
                    'Il sistema non ti chiede che lavoro stai facendo. Gli dici cosa hai davanti, e il resto lo trova lui.',
                    'Le domande sono due: **da dove arriva questa merce**, e **chi la aspetta**.',
                    'Le risposte fanno tre giri diversi. Qui sotto ci sono tutti e tre, con la pagina che li racconta per esteso.',
                ],
            },
            {
                title: 'Uno · è arrivato un bancale da un fornitore',
                body: [
                    'È il giro di ogni giorno. La merce arriva con una bolla, e un cliente l’aveva ordinata.',
                    'Prima **Banco**: leggi un capo, apri una cassetta, conta, stampa, conferma, marca pronta.',
                    'Poi **Carico**: porta le cassette in baia e conferma. Da lì entrano nel magazzino.',
                    'Per esteso: «Il giro del banco», poi «Portare le cassette in magazzino».',
                ],
            },
            {
                title: 'Due · sulla stessa bolla c’è merce che nessuno aspetta',
                body: [
                    'Capita spesso: sullo stesso ordine d’acquisto una parte è per un cliente, una parte è **comprata a scorta**.',
                    'La riconosci da sola: al posto del cliente c’è scritto **Magazzino**, e al posto dell’ordine **a magazzino · bolla**.',
                    'Stessa schermata, stessi gesti. Cambia solo dove finisce: quella cassetta in magazzino **si ferma**.',
                    'Per esteso: «La merce comprata a scorta».',
                ],
            },
            {
                title: 'Tre · la merce è già in casa',
                body: [
                    'Nessuna bolla, nessun ordine d’acquisto: i capi ci sono già e vanno messi dentro il magazzino.',
                    'Vai a **Negozio**. Si conta a pezzi, non a cassette: niente contenitore, niente etichette da stampare.',
                    'Dalla stessa schermata si chiede anche merce indietro.',
                    'Per esteso: «Il negozio: caricare e chiedere merce».',
                ],
            },
            {
                title: 'E se un cliente ha fretta?',
                body: [
                    'Oggi lo lavori **come tutti gli altri**: il software non distingue un ordine urgente dagli altri.',
                    'Il flusso è deciso e la schermata **Prelievi** esiste, ma non è ancora collegata al vero.',
                    'Per capire cosa cambierà: «Ordini express e prelievi».',
                ],
            },
            {
                title: 'Dove finisce il giro, oggi',
                body: [
                    'Ricezione, carico e negozio girano sui dati veri: quello che fai lì è successo davvero.',
                    'Scarico, stampa DTF, ricamo e fine linea hanno la schermata, ma girano su **dati di prova**.',
                    'Vuol dire che ci puoi provare i gesti, e che quello che vedi lì non è la merce vera. Per questo non hanno una pagina in questa guida.',
                ],
            },
        ],
    },
    {
        k: 'banco-schermata',
        station: 'banco',
        title: 'Come si legge una schermata',
        summary: 'Dove sono, cosa sto guardando, cosa succede dopo un tocco.',
        roles: TUTTI,
        sections: [
            {
                title: 'Le quattro cose che trovi sempre',
                body: [
                    'A sinistra il menu, con le tre lettere di ogni schermata. Si chiude per lasciare spazio.',
                    'In alto il titolo, con la campana degli avvisi.',
                    'Al centro il lavoro.',
                    'In basso a destra la risposta all’ultimo tocco.',
                ],
            },
            {
                title: 'La risposta a un tocco',
                body: [
                    'Ogni gesto riceve una risposta, in basso a destra.',
                    'Se è andata bene sparisce da sola dopo cinque secondi.',
                    'Se il sistema ha detto di no resta lì, finché non la chiudi con la ×. È voluto: quella frase serve.',
                ],
            },
            {
                title: 'Quando il sistema non sa',
                body: [
                    'Il sistema usa quattro parole, sempre le stesse.',
                    '**Sto caricando** — aspetta un attimo.',
                    '**Non c’è niente** — è vero che non c’è niente, non è un guasto.',
                    '**Non hai il permesso** — serve un ruolo che non hai.',
                    '**Si è rotto qualcosa** — c’è un tasto per riprovare.',
                    'Se un numero sparisce invece di mostrare zero, vuol dire «non lo so»: non è la stessa cosa di zero.',
                ],
            },
            {
                title: 'Entrare',
                body: [
                    'Nome utente e password sono personali, non della postazione.',
                    'Ogni tanto il sistema richiede la password prima di un gesto importante: è la firma, non un nuovo accesso. Vale cinque minuti.',
                    'Se ti serve una schermata che non vedi nel menu, chiedi il ruolo a chi amministra gli utenti.',
                ],
            },
        ],
    },
    {
        k: 'banco-giro',
        station: 'banco',
        title: 'Il giro del banco',
        summary: 'Dalla scatola alla cassetta pronta, in sei passi.',
        roles: TUTTI,
        screen: '/ricezione',
        sections: [
            {
                title: 'I sei passi, in ordine',
                figure: {
                    file: 'banco-ricezione.png',
                    alt: 'Il banco di ricezione: a sinistra il capo, al centro il gesto, a destra le righe attese.',
                    width: 1366,
                    height: 768,
                },
                steps: [
                    { do: 'Aggancia il capo.', then: 'Leggi il codice, o cerca l’articolo per nome.' },
                    {
                        do: 'Apri una cassetta.',
                        then: 'Leggi o scrivi il suo codice. Le cassette già usate da questa riga hanno un tasto.',
                    },
                    { do: 'Conta i capi.', then: 'Un tocco sulla colonna della taglia è un capo trovato.' },
                    { do: 'Stampa le etichette.', then: 'Una per taglia. Escono dopo il conteggio.' },
                    { do: 'Conferma il contenuto.', then: 'Scrive l’etichetta della cassetta.' },
                    { do: 'Marca la cassetta pronta per il magazzino.' },
                ],
            },
            {
                title: 'L’ordine lo trova il sistema',
                body: [
                    'Non scegli tu il documento: leggi il capo e il sistema aggancia l’ordine d’acquisto più vecchio che aspetta quell’articolo, in quella taglia e quel colore.',
                    'Se l’ordine cambia, te lo dice.',
                    'Se il capo non ha un codice a barre — capita con tre marchi su quattro — cerca l’articolo per nome. Escono le righe attese, dalla più vecchia.',
                ],
                refusals: [
                    {
                        rule: '2-bis',
                        says: 'Riga sconosciuta',
                        why: 'Quel capo non risulta atteso da nessun ordine aperto.',
                        fix: 'Controlla il codice. Se è giusto, cerca l’articolo per nome: forse la riga è di un altro arrivo.',
                    },
                ],
            },
            {
                title: 'Prima il capo, poi la cassetta',
                body: [
                    'Non si apre una cassetta prima di aver agganciato un capo: è il capo a dire cosa ci va dentro.',
                ],
                refusals: [
                    {
                        rule: '6',
                        says: 'Nessun capo agganciato',
                        why: 'Hai toccato una cassetta senza aver letto prima un capo.',
                        fix: 'Leggi il codice del capo, poi torna sulla cassetta.',
                    },
                ],
            },
        ],
    },
    {
        k: 'banco-contare',
        station: 'banco',
        title: 'Contare, correggere, dichiarare un fallato',
        summary: 'Un tocco è un capo. I due modi per sbagliarsi meno.',
        roles: TUTTI,
        screen: '/ricezione',
        sections: [
            {
                title: 'Contare',
                body: [
                    'Tocca la colonna della taglia: è un capo trovato nella scatola.',
                    'La colonna si riempie mentre conti. Quando la taglia è al completo il tasto si spegne.',
                ],
                refusals: [
                    {
                        rule: '7',
                        says: 'Taglia al completo',
                        why: 'Di quella taglia è già arrivato tutto quello che era ordinato.',
                        fix: 'Controlla di non aver contato due volte. Se i capi ci sono davvero, sono in più rispetto all’ordine: avvisa l’ufficio acquisti.',
                    },
                    {
                        rule: '7',
                        says: 'Taglia non attesa',
                        why: 'Quella taglia non compare sull’ordine.',
                        fix: 'Ricontrolla l’etichetta del capo. Può essere un capo di un altro ordine.',
                    },
                ],
            },
            {
                title: '− correggi',
                body: [
                    'Hai contato un capo di troppo: premi «− correggi», poi tocca la taglia.',
                    'Toglie un capo **dalla cassetta aperta**, non da un totale.',
                    'È un modo a colpo singolo: vale per un tocco solo, poi si spegne. Esc annulla.',
                ],
                refusals: [
                    {
                        rule: '9',
                        says: 'Niente da togliere',
                        why: 'In quella taglia, in questa cassetta, non c’è nessun capo.',
                        fix: 'Controlla di essere sulla cassetta giusta.',
                    },
                ],
            },
            {
                title: '✗ fallato',
                body: [
                    'Il capo è arrivato difettoso dal fornitore: premi «✗ fallato», poi tocca la taglia.',
                    'Il capo **non entra in cassetta**. Resta non ricevuto, e l’ordine d’acquisto resta aperto per quella quantità.',
                    'Scatta la foto se puoi: è la prova che il fornitore riceve. Si chiede, non si pretende.',
                    'La segnalazione arriva **subito in ufficio**, alla pagina Qualità. Chi la prende in carico compare accanto alla riga.',
                    'La mail all’ufficio acquisti parte solo se qualcuno l’ha accesa in Impostazioni.',
                    'Il fallato non si annulla. Il «− correggi» sì.',
                ],
            },
            {
                title: 'La foto del capo',
                body: [
                    'Sulle righe senza immagine di catalogo il sistema chiede una foto: «📷 scatta la foto».',
                    'Si chiede, non si pretende: la cassetta si conferma anche senza. Una webcam guasta non ferma il banco.',
                    'Serve a riconoscere l’articolo la volta dopo.',
                ],
                refusals: [
                    {
                        rule: '38-bis',
                        says: 'Nessun capo agganciato',
                        why: 'La fotocamera si apre sul capo che stai ricevendo, e non ce n’è uno.',
                        fix: 'Aggancia prima un capo.',
                    },
                ],
            },
        ],
    },
    {
        k: 'banco-cassette',
        station: 'banco',
        title: 'Le cassette',
        summary: 'Quanto ci sta, cosa ci sta insieme, come si chiude.',
        roles: TUTTI,
        screen: '/ricezione',
        sections: [
            {
                title: 'Le due regole della cassetta',
                body: [
                    'Una cassetta tiene **un solo cliente e un solo articolo**.',
                    'Una cassetta tiene i capi decisi dall’ufficio: **24**, se nessuno l’ha cambiato. Piena, se ne apre un’altra e la riga prosegue.',
                    'Si apre solo una cassetta che l’ufficio ha **nell’elenco**. Una cassetta che contiene già merce si riapre sempre.',
                ],
                refusals: [
                    {
                        rule: '4',
                        says: 'Cassetta di un altro articolo',
                        why: 'Dentro c’è già merce di un altro articolo o di un altro cliente.',
                        fix: 'Prendi un’altra cassetta. Il messaggio ti dice cosa c’è dentro a quella.',
                    },
                    {
                        rule: '5',
                        says: 'Cassetta piena',
                        why: 'Ci sono già tutti i capi che la cassetta tiene.',
                        fix: 'Apri un’altra cassetta: la riga si spezza, ed è normale.',
                    },
                    {
                        rule: '6-bis',
                        says: 'Cassetta sconosciuta',
                        why: 'Quel codice non è nell’elenco delle cassette dell’ufficio.',
                        fix: 'Controlla il codice. Se la cassetta è nuova, chiedi all’ufficio di aggiungerla.',
                    },
                    {
                        rule: '6-bis',
                        says: 'Cassetta fuori servizio',
                        why: 'L’ufficio l’ha tolta dal giro. Il messaggio dice perché.',
                        fix: 'Prendi un’altra cassetta.',
                    },
                ],
            },
            {
                title: 'Confermare il contenuto',
                body: [
                    'Il tasto «Conferma» sta sulla tessera della cassetta, in fondo alla colonna di sinistra.',
                    'Confermare scrive l’etichetta della cassetta: cosa c’è dentro adesso.',
                    'Senza conferma la cassetta non va a magazzino.',
                    'Diventa verde e dice «✓ Riconferma»: se dentro cambia qualcosa, riconferma.',
                ],
                refusals: [
                    {
                        rule: '3-ter',
                        says: 'Etichette mancanti',
                        why: 'Qualche taglia della cassetta non ha ancora la sua etichetta.',
                        fix: 'Stampa le etichette, poi conferma. Il tasto stampa dice quali mancano.',
                    },
                    {
                        rule: '10',
                        says: 'Contenuto non confermato',
                        why: 'Hai marcato pronta una cassetta che nessuno ha confermato.',
                        fix: 'Conferma il contenuto, poi marcala pronta.',
                    },
                ],
            },
            {
                title: 'Svuotare',
                body: [
                    '«Svuota» libera la cassetta e disfa tutto: i capi tornano da ricevere, le etichette già stampate sono da buttare, la conferma cade.',
                    'Chiede conferma prima di farlo, e dice cosa perdi.',
                    'Serve quando hai sbagliato cassetta. Non serve per correggere un capo: per quello c’è «− correggi».',
                ],
            },
        ],
    },
    {
        k: 'banco-etichette',
        station: 'banco',
        title: 'Le etichette e il QR',
        summary: 'Due fogli diversi, e cosa apre il quadrato.',
        roles: TUTTI,
        screen: '/ricezione',
        sections: [
            {
                title: 'Prima si conta, poi si stampa',
                body: [
                    'L’etichetta porta sopra la quantità, e la quantità la scopri contando.',
                    'Una etichetta **per taglia e per cassetta**. Due taglie nella stessa cassetta fanno due etichette.',
                    'Se conti ancora quella taglia, la sua etichetta decade: il tasto torna a dire «Stampa».',
                ],
                refusals: [
                    {
                        rule: '3',
                        says: 'Niente da stampare',
                        why: 'Le etichette di quella cassetta ci sono già tutte.',
                        fix: 'Se il foglio si è strappato, premi lo stesso tasto: quando è verde ristampa gli stessi codici.',
                    },
                ],
            },
            {
                title: 'Ristampare',
                body: [
                    'Il tasto delle etichette è verde quando ci sono già tutte: premerlo rifà gli stessi fogli, con gli stessi codici a barre.',
                    'Se hai contato altri capi dopo aver stampato, la ristampa si rifiuta: quel foglio non direbbe più il vero. Stampa invece di ristampare.',
                ],
            },
            {
                title: 'I due fogli',
                body: [
                    '**L’etichetta dei capi** resta attaccata alla merce. Porta cliente, articolo, taglia, quantità e il codice del gruppo.',
                    '**L’etichetta della cassetta** sta sulla cassetta e si riscrive a ogni conferma. Porta il codice, il cliente, quanti capi e il QR.',
                    'Sull’etichetta della cassetta non c’è l’articolo: una cassetta terrà più righe dello stesso ordine, e scriverne una sola sarebbe scrivere il falso.',
                ],
            },
            {
                title: 'Il QR',
                body: [
                    'Inquadra il QR col telefono: si apre la pagina della cassetta, senza dover entrare nel sistema.',
                    'Dice cosa c’è dentro, di chi è, e quali etichette sono state scritte.',
                    'Dal banco arrivi alla stessa pagina toccando il nome della cassetta sulla sua tessera.',
                    'Se il foglio è vecchio la pagina te lo dice, e ti porta al contenuto di adesso. Il numero di riscrittura sul foglio serve a questo.',
                ],
            },
        ],
    },
    {
        k: 'banco-carico',
        station: 'banco',
        title: 'Portare le cassette in magazzino',
        summary: 'Dalla cassetta pronta al posto dentro la macchina.',
        roles: TUTTI,
        screen: '/carico',
        sections: [
            {
                title: 'Il carico è un atto a parte',
                body: [
                    'Una cassetta marcata pronta al banco **non è ancora a magazzino**: aspetta in coda.',
                    'Il carico è il gesto di portarla in baia e confermarla. Finché non confermi, per il magazzino quella merce non esiste.',
                    'La coda dice di chi è la merce, dove va — reparto, neutro, a scorta — e da quando aspetta.',
                ],
            },
            {
                title: 'I tre passi',
                steps: [
                    {
                        do: 'Porta le cassette in baia.',
                        then: 'Toccale nella coda: passano fra le scansionate.',
                    },
                    {
                        do: 'Controlla che in baia ci sia quello che hai portato.',
                        then: 'Se ne hai messa una di troppo, toccala per toglierla.',
                    },
                    { do: 'Conferma l’inserimento.', then: 'Da qui la missione parte verso il magazzino.' },
                ],
            },
            {
                title: 'Dove finisce la merce, e chi lo decide',
                body: [
                    'Il posto — macchina, cassetto, scomparto — **lo sceglie il magazzino**, non tu. Noi non lo indichiamo mai.',
                    'Il magazzino risponde subito «ricevuto», ma la merce entra quando l’operatore alla baia mette dentro il cassetto e conferma.',
                    'In quel mezzo la cassetta è **in viaggio**: affidata al magazzino e non ancora riposta. Il riquadro «Dove sono finite» lo dice, e quando l’esito torna mostra lo scomparto.',
                    'Se resta «il WMS non ha ancora detto dove», premi «Aggiorna dal WMS».',
                ],
            },
            {
                title: 'Quando qualcosa non torna',
                refusals: [
                    {
                        rule: '39',
                        says: 'Articolo sconosciuto',
                        why: 'Il codice del negozio non risulta a catalogo.',
                        fix: 'Scegli l’articolo dall’elenco invece di scriverlo.',
                    },
                    {
                        rule: '39',
                        says: 'Baia di negozio vuota',
                        why: 'Hai confermato un carico di negozio senza aver messo dentro niente.',
                        fix: 'Cerca l’articolo e di’ quanti pezzi sono.',
                    },
                    {
                        rule: '12',
                        says: 'Cassetta sconosciuta',
                        why: 'Quel codice non risulta arrivato dal banco.',
                        fix: 'Controlla di averla marcata pronta al banco. Se il banco dice «non ancora passata al magazzino», rifai la marcatura: la consegna si riprova da sé.',
                    },
                    {
                        rule: '13',
                        says: 'Baia vuota',
                        why: 'Hai confermato senza aver scansionato niente.',
                        fix: 'Porta prima le cassette in baia.',
                    },
                    {
                        rule: '12',
                        says: 'Il magazzino non conosce questo articolo',
                        why: 'Il magazzino ora chiama gli articoli con lo SKU di DecoNetwork, e questa variante non ne ha uno utilizzabile (manca, è N/A, è più lungo di 50 caratteri o è uguale su due varianti) oppure non è ancora nella nostra copia di DecoNetwork.',
                        fix: 'Compila o correggi lo SKU della variante in DecoNetwork (o chiedilo in ufficio). La nostra copia si aggiorna in circa 15 minuti: poi riprova. Le cassette e le righe restano in baia.',
                    },
                    {
                        rule: '12',
                        says: 'Il magazzino ha rifiutato il carico',
                        why: 'Il WMS ha detto di no — spesso perché è pieno, o perché quella cassetta risulta già dentro.',
                        fix: 'Le cassette restano in baia: leggi il motivo e riprova. Se dice pieno, serve fare spazio.',
                    },
                ],
            },
        ],
    },
    {
        k: 'banco-scorta',
        station: 'banco',
        title: 'La merce comprata a scorta',
        summary: 'Arrivata con la bolla, ma nessun cliente la aspetta.',
        roles: TUTTI,
        screen: '/ricezione',
        sections: [
            {
                title: 'Cos’è',
                body: [
                    'Merce ordinata al fornitore per **riempire il magazzino**, non per un ordine di un cliente.',
                    'Arriva sulla stessa bolla della merce dei clienti: sullo stesso ordine d’acquisto una riga può essere per un cliente e un’altra a scorta.',
                    'Non è il negozio. Il negozio è merce **già in casa**, senza bolla: sta in un’altra schermata e si conta a pezzi.',
                ],
            },
            {
                title: 'Come la riconosci',
                figure: {
                    file: 'banco-scorta.png',
                    alt: 'Il banco con la bolla 552781 aperta: le due righe a destra dicono Magazzino al posto del cliente, «a magazzino · bolla 552781» al posto dell’ordine e «A SCORTA · RESTA IN MAGAZZINO» come destino.',
                    width: 1366,
                    height: 768,
                },
                body: [
                    'Nell’elenco a destra, al posto del nome del cliente c’è scritto **Magazzino**.',
                    'Sotto, al posto del numero d’ordine, c’è **a magazzino · bolla 552283**: quello è il numero dell’ordine d’acquisto, cioè il numero che hai sul foglio.',
                    'Come destino la riga dice **a scorta · resta in magazzino**.',
                    'Quando l’hai agganciata, in alto la voce «Ordine» dice la stessa cosa: «a magazzino · bolla 552283».',
                ],
            },
            {
                title: 'Si lavora come tutte le altre',
                body: [
                    'Stessi sei passi del giro normale: aggancia, apri la cassetta, conta, stampa, conferma, marca pronta.',
                    'Stesse regole della cassetta: la stessa capienza, un solo articolo, e qui il «cliente» della cassetta è Magazzino.',
                    'Il banco ti **chiede la foto** del capo: la merce a scorta non ha una stampa che la faccia riconoscere, e la foto è quello che resta.',
                    'Poi si carica dal **Carico**, come tutte: nella coda la cassetta porta scritto «A SCORTA · resta in magazzino».',
                ],
            },
            {
                title: 'Dove si ferma',
                body: [
                    'Questa è l’unica differenza vera, ed è alla fine: la cassetta **resta in magazzino**.',
                    'Non va in produzione, perché nessuno la decora. Non va a fine linea, perché nessuno la aspetta.',
                    'Resta a scaffale finché un ordine futuro non la chiama.',
                    'Una cassetta neutra d’ordine assomiglia a questa — anche lei non passa dai reparti — ma quella attraversa il magazzino e va a fine linea. Guarda la scritta, non l’articolo.',
                ],
            },
            {
                title: 'Se un capo lo aspettano in due',
                body: [
                    'Può succedere che lo stesso articolo sia atteso da un cliente **e** comprato a scorta.',
                    'In quel caso il capo che hai in mano va al cliente: nell’elenco le righe a scorta stanno **in fondo**, dopo quelle degli ordini.',
                    'Non è un caso e non va corretto: il cliente ha una data di consegna, il magazzino no.',
                ],
            },
        ],
    },
    {
        k: 'banco-negozio',
        station: 'banco',
        title: 'Il negozio: caricare e chiedere merce',
        summary: 'Merce già a stock, contata a pezzi e non a cassette.',
        roles: TUTTI,
        screen: '/negozio',
        sections: [
            {
                title: 'Qui non ci sono cassette',
                body: [
                    'Il negozio è la merce **già a stock**: nessun ordine di acquisto l’ha portata, e nessun cliente la aspetta.',
                    'Si carica a pezzi: «trenta di questo articolo». Niente contenitore, niente etichetta da stampare.',
                    'L’articolo comprende la taglia. È per questo che poi si possono chiedere tre nere taglia M, e non solo tre nere.',
                ],
            },
            {
                title: 'La chiave non si scrive mai',
                body: [
                    'Cerca per quello che leggi sul capo: nome, colore, taglia, codice.',
                    'Il codice in piccolo sotto l’articolo è quello che va al magazzino. **Lo porta la riga che tocchi**: non si digita.',
                    'Scriverlo a mano vorrebbe dire poter mettere dentro merce con un codice sbagliato. Quella merce non la ritrova più nessuno.',
                ],
            },
            {
                title: 'I tre passi del carico',
                steps: [
                    { do: 'Cerca l’articolo.', then: 'Scrivi quello che vedi: «polo nera M».' },
                    {
                        do: 'Di’ quanti pezzi sono.',
                        then: 'Il contatore in alto vale per tutti: toccalo prima di scegliere.',
                    },
                    {
                        do: 'Tocca «Carica».',
                        then: 'La riga va in baia. Tocca ancora per aggiungerne altri: si sommano.',
                    },
                    {
                        do: 'Conferma il carico.',
                        then: 'Da qui la missione parte. Finché non confermi, per il magazzino quella merce non c’è.',
                    },
                ],
            },
            {
                title: 'Chiedere merce indietro',
                body: [
                    '«Chiedi» è l’altro tasto sulla stessa riga: il magazzino tira fuori quei pezzi.',
                    'Aspetta la baia prima di dire a qualcuno che la merce arriva: il magazzino risponde «ricevuto» anche quando poi non trova niente.',
                    'Può uscirne **più** di quanto hai chiesto: esce il cassetto, non i tre capi. Quello che avanza va rimesso dentro.',
                ],
            },
            {
                title: 'Quanti ce ne sono dentro',
                body: [
                    'Accanto a ogni articolo c’è quanti ne dice il magazzino.',
                    '**«Mai letto» non è «zero».** Vuol dire che il magazzino non ha ancora parlato — finché le macchine non sono installate, dice sempre così.',
                    'Uno zero al posto di «mai letto» farebbe sembrare vuoto un magazzino che nessuno ha ancora interrogato.',
                ],
            },
            {
                title: 'Quando qualcosa non torna',
                refusals: [
                    {
                        rule: '39',
                        says: 'Articolo sconosciuto',
                        why: 'Quel codice non risulta a catalogo.',
                        fix: 'Scegli l’articolo dall’elenco invece di scriverlo.',
                    },
                    {
                        rule: '39',
                        says: 'Baia di negozio vuota',
                        why: 'Hai confermato senza aver messo dentro niente.',
                        fix: 'Cerca l’articolo e di’ quanti pezzi stai caricando.',
                    },
                    {
                        rule: '39',
                        says: 'Quantità non valida',
                        why: 'Un carico si dichiara a pezzi interi, da uno in su.',
                        fix: 'Alza il contatore almeno a uno.',
                    },
                    {
                        rule: '39',
                        says: 'Il magazzino non conosce questo articolo',
                        why: 'Il magazzino ora chiama gli articoli con lo SKU di DecoNetwork, e questa variante non ne ha uno utilizzabile (manca, è N/A, è più lungo di 50 caratteri o è uguale su due varianti) oppure non è ancora nella nostra copia di DecoNetwork.',
                        fix: 'Compila o correggi lo SKU della variante in DecoNetwork (o chiedilo in ufficio). La nostra copia si aggiorna in circa 15 minuti: poi riprova. Le cassette e le righe restano in baia.',
                    },
                    {
                        rule: '39',
                        says: 'Il magazzino ha rifiutato il carico',
                        why: 'Il WMS ha detto di no — spesso perché è pieno.',
                        fix: 'Le righe restano in baia: leggi il motivo e riprova. Se dice pieno, serve fare spazio.',
                    },
                    {
                        rule: '39',
                        says: 'La baia è cambiata',
                        why: 'Qualcun altro ha lavorato sulla stessa baia mentre confermavi.',
                        fix: 'Guarda cosa c’è in baia adesso, poi conferma di nuovo.',
                    },
                    {
                        rule: '40',
                        says: 'Il magazzino non conosce questo articolo',
                        why: 'Il magazzino ora chiama gli articoli con lo SKU di DecoNetwork, e questa variante non ne ha uno utilizzabile (manca, è N/A, è più lungo di 50 caratteri o è uguale su due varianti) oppure non è ancora nella nostra copia di DecoNetwork.',
                        fix: 'Compila o correggi lo SKU della variante in DecoNetwork (o chiedilo in ufficio). La nostra copia si aggiorna in circa 15 minuti: poi riprova. Le cassette e le righe restano in baia.',
                    },
                    {
                        rule: '40',
                        says: 'Il magazzino ha rifiutato il prelievo',
                        why: 'Il WMS non ha potuto tirare fuori quella merce.',
                        fix: 'Leggi il motivo. Se non ce n’è abbastanza, prendine meno.',
                    },
                ],
            },
        ],
    },
    {
        k: 'banco-express',
        station: 'banco',
        title: 'Ordini express e prelievi',
        summary: 'Il flusso è deciso, la schermata c’è. I dati veri non ancora.',
        roles: ['magazzino'],
        screen: '/picking',
        sections: [
            {
                title: 'Leggi prima questo',
                body: [
                    'Questa schermata **non è ancora attiva**: c’è, si apre, e resta vuota.',
                    'Il sistema oggi non sa quali ordini sono express, e non sa cosa manchi a un ordine: quei due dati non arrivano ancora da DecoNetwork.',
                    'Quindi «Prelievi» dice sempre «Nessun ordine fermo», anche quando in reparto un ordine è fermo davvero.',
                    'Nel frattempo un ordine urgente si lavora come tutti gli altri. Questa pagina serve a sapere cosa cambierà, non a lavorarci oggi.',
                ],
            },
            {
                title: 'Cosa sono i due filoni',
                body: [
                    'Qui finisce la merce che **non arriva da nessun ordine d’acquisto**: è già a stock, dentro il magazzino.',
                    '**Express**: la merce dell’ordine c’è già tutta. Non si aspetta nessun fornitore, si va a prenderla.',
                    '**Parziali**: una parte è arrivata dal fornitore, il resto era già in casa. Manca solo quel pezzo.',
                ],
            },
            {
                title: 'La postazione',
                body: [
                    'Bancone, fotocamera e stampante — con la stampante **vicino al Modula**, così i capi si etichettano al prelievo, una etichetta per taglia.',
                    'Per gli articoli voluminosi si genera una **cassetta virtuale**: un codice caricabile sul Modula, senza contenitore fisico. Generarla vale come prelievo della riga.',
                ],
            },
            {
                title: 'La riga la genera il sistema',
                body: [
                    'Nessuno scrive a mano una riga di prelievo: nasce quando quel che c’è in magazzino non corrisponde a quel che l’ordine chiede.',
                    'Serve a una cosa sola: finché una di quelle righe non è presa, **l’ordine non entra in produzione**.',
                    'È il posto da cui si sblocca, ed è per questo che le cose ferme si guardano qui e non nel piano.',
                ],
            },
            {
                title: 'I gesti, riga per riga',
                body: [
                    'La figura qui sotto è fatta con **merce inventata**, per far vedere com’è la schermata quando ha qualcosa dentro.',
                    'Oggi, con i dati veri, quell’elenco è vuoto.',
                ],
                figure: {
                    file: 'banco-prelievi.png',
                    alt: 'La schermata Prelievi con due ordini fermi di prova, uno express e uno urgente: per ogni riga i tasti «Preleva» e «Cassetta virtuale», e sotto «Autorizza il parziale».',
                    width: 1366,
                    height: 768,
                },
                steps: [
                    { do: 'Leggi l’elenco degli ordini fermi.', then: 'In testata c’è quanti sono.' },
                    {
                        do: 'Vai a prendere la merce e tocca «Preleva».',
                        then: 'La riga si chiude. Quando l’ordine è completo, te lo dice.',
                    },
                    {
                        do: 'Se l’articolo è ingombrante, tocca «Cassetta virtuale».',
                        then: 'Nasce un codice tipo V01, caricabile sul magazzino senza cassetta vera. Vale come prelievo.',
                    },
                ],
            },
            {
                title: 'La postazione',
                body: [
                    'Bancone, fotocamera e stampante, con la stampante **vicino alla Modula**.',
                    'I capi si etichettano al prelievo, una etichetta per taglia.',
                ],
            },
            {
                title: 'Far partire un ordine incompleto',
                body: [
                    '«Autorizza il parziale» lascia partire un ordine a cui manca ancora della merce.',
                    'Solo l’amministratore, e chiede la password: è una firma, e resta scritta sull’ordine col nome e la data fino alla spedizione.',
                    'Con la deroga resta scritto anche **cosa** mancava.',
                ],
                refusals: [
                    {
                        rule: '20-ter',
                        says: 'Ordine già completo',
                        why: 'A quell’ordine non manca niente.',
                        fix: 'Non serve deroga: l’ordine può partire da solo.',
                    },
                    {
                        rule: '20-ter',
                        says: 'Deroga già data',
                        why: 'Qualcuno l’ha già autorizzato.',
                        fix: 'Il messaggio dice chi e quando. Non va rifatta.',
                    },
                ],
            },
            {
                title: 'Quando qualcosa non torna',
                refusals: [
                    {
                        rule: '32',
                        says: 'Ordine sconosciuto',
                        why: 'Quell’ordine non è nel piano del giorno.',
                        fix: 'Ricarica la schermata: il piano si rifà, e forse quell’ordine è già passato oltre.',
                    },
                    {
                        rule: '32',
                        says: 'Niente da prelevare',
                        why: 'Su quell’ordine quell’articolo non risulta da prendere, o l’ha già preso qualcuno.',
                        fix: 'Guarda l’elenco aggiornato prima di andare a prendere altro.',
                    },
                ],
            },
        ],
    },
    {
        k: 'banco-no',
        station: 'banco',
        title: 'Quando il sistema dice di no',
        summary: 'Non è un guasto: è una regola. Come si legge e cosa si fa.',
        roles: TUTTI,
        sections: [
            {
                title: 'Come si legge un rifiuto',
                body: [
                    'Un rifiuto porta sempre tre cose: un titolo, il motivo, e il numero della regola.',
                    'Resta a schermo finché non lo chiudi: le conferme spariscono da sole, i rifiuti no.',
                    'Se ti sembra sbagliato non forzare: chiamalo col suo numero. È quello che serve a chi te lo deve sistemare.',
                ],
            },
            {
                title: 'I rifiuti del banco',
                refusals: [
                    {
                        rule: '2-bis',
                        says: 'Riga sconosciuta',
                        why: 'Nessun ordine aperto aspetta quel capo.',
                        fix: 'Ricontrolla il codice, o cerca l’articolo per nome.',
                    },
                    {
                        rule: '3',
                        says: 'Niente da stampare',
                        why: 'Le etichette ci sono già tutte.',
                        fix: 'Il tasto verde ristampa gli stessi fogli.',
                    },
                    {
                        rule: '3-bis',
                        says: 'Etichetta non più valida',
                        why: 'Hai contato altri capi dopo aver stampato: il foglio dichiara una quantità che non c’è più.',
                        fix: 'Stampa, non ristampare: serve un codice nuovo.',
                    },
                    {
                        rule: '3-ter',
                        says: 'Etichette mancanti',
                        why: 'Una taglia della cassetta non ha etichetta.',
                        fix: 'Stampa, poi conferma.',
                    },
                    {
                        rule: '4',
                        says: 'Cassetta di un altro articolo',
                        why: 'La cassetta tiene un solo cliente e un solo articolo.',
                        fix: 'Prendi un’altra cassetta.',
                    },
                    {
                        rule: '5',
                        says: 'Cassetta piena',
                        why: 'Ci sono già tutti i capi che la cassetta tiene.',
                        fix: 'Aprine un’altra: la riga si spezza.',
                    },
                    {
                        rule: '6',
                        says: 'Nessun capo agganciato',
                        why: 'È il capo a dire cosa va nella cassetta.',
                        fix: 'Leggi prima il codice del capo.',
                    },
                    {
                        rule: '6',
                        says: 'Nessuna cassetta aperta',
                        why: 'Stai contando senza aver aperto una cassetta.',
                        fix: 'Tocca la cassetta in cui stai mettendo i capi.',
                    },
                    {
                        rule: '7',
                        says: "Più dell'ordinato",
                        why: 'Ricevuti e fallati insieme supererebbero la quantità ordinata.',
                        fix: 'Ricontrolla il conteggio. Se la merce c’è davvero, avvisa l’ufficio acquisti.',
                    },
                    {
                        rule: '9',
                        says: 'Niente da togliere',
                        why: 'In quella taglia, in questa cassetta, non c’è niente.',
                        fix: 'Controlla di essere sulla cassetta giusta.',
                    },
                    {
                        rule: '10',
                        says: 'Contenuto non confermato',
                        why: 'Una cassetta va a magazzino solo dopo la conferma.',
                        fix: 'Conferma, poi marca pronta.',
                    },
                    {
                        rule: '12',
                        says: 'Cassetta sconosciuta',
                        why: 'In baia hai scansionato un codice che dal banco non è mai arrivato.',
                        fix: 'Marcala pronta al banco. Se il banco dice «non ancora passata al magazzino», rifai il gesto: la consegna si riprova.',
                    },
                    {
                        rule: '13',
                        says: 'Baia vuota',
                        why: 'Hai confermato il carico senza aver scansionato niente.',
                        fix: 'Porta prima le cassette in baia.',
                    },
                    {
                        rule: '38-bis',
                        says: 'Nessun capo agganciato',
                        why: 'La fotocamera scatta il capo che stai ricevendo.',
                        fix: 'Aggancia prima un capo.',
                    },
                ],
            },
            {
                title: 'Le altre risposte del sistema',
                body: [
                    'Non tutto quello che si ferma è un rifiuto. Ce ne sono altre tre, e vogliono cose diverse.',
                    '**Campi sbagliati** — qualcosa che hai scritto non va. Il messaggio dice quale.',
                    '**Riprova fra poco** — un sistema esterno non risponde. Aspetta e rifai il gesto.',
                    '**Guasto, con un codice corto** — è un problema nostro. Scrivi quel codice e chiamaci: con quello si trova cos’è successo.',
                ],
            },
        ],
    },
];
//# sourceMappingURL=bench.js.map